import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { vendorUsersQueryKey } from "~/shared/hooks/use-vendor-users.ts";
import { vendorUserMutationResponseSchema } from "~/shared/schemas/vendor-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

import type { VendorUserFormData } from "../components/add-user-modal/add-user-modal.tsx";
import { extractVendorUserError } from "./extract-vendor-user-error.ts";

type AddVendorUserVariables = VendorUserFormData & {
	customerId: number;
};

export const useAddVendorUser = () => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();
	const { isInternal } = useDeploymentType();

	const { mutateAsync: addVendorUser, isPending } = useMutation({
		mutationFn: async (newUser: AddVendorUserVariables) => {
			const route = isInternal
				? apiRoutes.INTERNALADDVENDOR.replace(
						":customerId",
						String(newUser.customerId),
					)
				: apiRoutes.ADDVENDOR;
			const resp = await postAndValidate(
				route,
				newUser,
				vendorUserMutationResponseSchema,
				{ onError: extractVendorUserError },
			);
			if (!resp.success) {
				throw new Error(resp.error);
			}
			return resp.data;
		},
		onSuccess: () => {
			showSuccess("Vendor user added successfully");
			// Fire-and-forget so the modal closes immediately; the grid's
			// isFetching state drives the skeleton loaders during refetch.
			void queryClient.invalidateQueries({ queryKey: vendorUsersQueryKey });
		},
		onError: (error: unknown) => {
			showError(
				error instanceof Error && error.message
					? error.message
					: "Failed to add vendor user",
			);
		},
	});

	return { addVendorUser, isPending };
};
